import moment, { Moment } from 'moment';
import { User } from 'api/models';
import { SprintPoints } from 'api/sprint-planning/types';
import { Field, FieldOption } from 'api/tasks';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { Task } from 'components/molecules/kanban-board/Kanban/type';
import {
  AssigneeField,
  AssigneeValue,
  FieldType,
  FilterableField,
  filterableFieldType,
} from 'components/organisms/tasks/fields';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { isNonWorkingDay } from 'components/organisms/timesheets/common/utils';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { FilterGroupType } from 'components/pages/types';
import { userOption } from 'utils/project-fields/construct-user-option';
import { unreachable } from 'utils/unreachable';

export const populateUsersMetaInFields = (
  fields: Field[],
  users: User[],
): Field[] => {
  return (fields ?? []).map((field) => {
    const type = filterableFieldType(field);

    if (type === FieldType.MultiUser || type === FieldType.SingleUser) {
      return {
        ...field,
        metaData: {
          ...field.metaData,
          options: users?.map(userOption) as FieldOption<number>[],
        },
      };
    }

    return field;
  });
};

export const restrictToUser =
  (assignee: AssigneeField) =>
  (filter: GroupedFilter) =>
  (userId: AssigneeValue): GroupedFilter => ({
    ...filter,
    groupCriteria: [
      ...filter.groupCriteria,
      {
        criteria: {
          field: assignee,
          type: SelectFilterOperationType.IsOneOf,
          value: [userId],
        },
        id: FilterGroupType.Task,
      },
    ],
  });

export const constructValueBasedOnFieldType = (field: Field, value: any) => {
  const type = filterableFieldType(field);

  switch (type) {
    case FieldType.SingleUser:
    case FieldType.SingleSelect:
    case FieldType.MultiUser:
    case FieldType.MultiSelect:
    case FieldType.Boolean:
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Assignee:
      return value;
    case FieldType.Date:
      return moment(value).format(API_DATE_FORMAT);
    case FieldType.Number:
      return Number(value);
    case FieldType.Estimate:
      // estimate is not an actual custom field type and this case is not possible. In future if it is needed, handle it accordingly
      return unreachable(type as never);
    default:
      return unreachable(type as never);
  }
};

export const isSprintExtraField = (
  pointType: SprintPoints | undefined,
  isSprintPlanningFeatureEnabled: boolean,
  selectedPointType?: SprintPoints,
  pointsEnabled?: boolean,
): boolean => {
  if (!isSprintPlanningFeatureEnabled || !pointsEnabled) {
    return !!pointType;
  }

  return pointType != null && pointType !== selectedPointType;
};

export const getDateBeforeSomeNumberOfWorkingDays = (
  dueDate: Moment,
  numberOfDays: number,
  workingDays: WorkingDaysConfiguration,
): Moment => {
  let daysLeft = numberOfDays;
  let currentDate = dueDate.clone();

  while (daysLeft > 0) {
    currentDate = currentDate.subtract(1, 'day');
    if (!isNonWorkingDay(currentDate, workingDays)) {
      daysLeft -= 1;
    }
  }

  return currentDate;
};

export const isWeekend = (date: Moment): boolean => {
  return date.day() === 0 || date.day() === 6;
};

export const filterDuplicateTasks = (tasks: Task[]) => {
  const uniqueTasks: Task[] = [];

  tasks?.forEach((task) => {
    if (!uniqueTasks.find((t) => task.id === t.id)) {
      uniqueTasks.push(task);
    }
  });

  return uniqueTasks;
};

export const filterStatus = (field: FilterableField | Field) =>
  field.fieldName.toLowerCase() !== 'status';
